import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import AnimatedButton from '../../components/ui/AnimatedButton';
import SoftShape from '../../components/decorations/SoftShape';
import DecorativeCloud from '../../components/decorations/DecorativeCloud';
import SparkleCluster from '../../components/decorations/SparkleCluster';
import MindoraCharacter from '../../components/character/MindoraCharacter';

interface Props {
  onNext: () => void;
}

export default function WelcomeHookScreen({ onNext }: Props) {
  const reduced = useReducedMotion();
  const { play } = useSoundEffects();

  // Play companion sound on mount (600ms delay)
  useEffect(() => {
    const timer = setTimeout(() => play('happyHum'), 600);
    return () => clearTimeout(timer);
  }, [play]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12 relative overflow-hidden">
      {/* Background decorations */}
      <SoftShape
        variant="blob1"
        size={300}
        color="var(--kid-dandelion)"
        className="absolute -top-20 -left-20 opacity-10"
      />
      <SoftShape
        variant="blob2"
        size={250}
        color="var(--kid-violet)"
        className="absolute -bottom-16 -right-16 opacity-10"
      />
      <DecorativeCloud size={80} className="absolute top-8 right-8 opacity-30" />
      <DecorativeCloud size={60} variant="wispy" className="absolute top-16 left-12 opacity-20" />

      {/* Banner image with Mindora overlay */}
      <motion.div
        className="relative mb-8"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        <img
          src="/banner.png"
          alt="Children learning together"
          className="max-w-sm w-full rounded-kid shadow-kid-lg"
        />
        {/* Mindora waving on banner */}
        <motion.div
          className="absolute -bottom-6 -right-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduced ? { duration: 0.3, delay: 0.3 } : { ...springs.bouncy, delay: 0.4 }}
        >
          <MindoraCharacter mood="waving" size="lg" />
        </motion.div>
        <SparkleCluster count={6} spread={60} className="absolute -top-4 -right-4" />
      </motion.div>

      {/* Headline — emotional punch, no diluting subtext */}
      <motion.h1
        className="font-display text-kid-3xl text-kid-text text-center mb-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.gentle, delay: 0.3 }}
      >
        Mornings without the meltdowns.
      </motion.h1>

      {/* CTA — tangerine (energy color) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.2 } : { ...springs.gentle, delay: 0.5 }}
      >
        <AnimatedButton
          variant="marshmallow"
          className="btn-marshmallow-tangerine"
          onClick={onNext}
        >
          See how it works
        </AnimatedButton>
      </motion.div>
    </div>
  );
}
